import React, { useEffect, useMemo, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import {
  faClose,
  faBoxOpen,
  faBoxesStacked,
  faLayerGroup,
  faPlus,
  faTags,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { useTranslation } from "react-i18next"
import GetSpace from "../../../utils/space"
import Button from "../../../components/Button/Button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ListSelections } from "../../../components/ListSelections/ListSelections"
import { CountElements } from "../../../components/CountElements/CountElements"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { ApplyPromotionRequest } from "../../../hooks/Promotions/useApplyPromotions"
import { EPromotionApplyTargetType } from "../../../Enum/EPromotionApplyTargetType"
import { useGetProducts } from "../../../hooks/Products/useGetProducts"
import { useGetCatalogItems } from "../../../hooks/CatalogItems/useGetCatalogItems"
import { useGetCombo } from "../../../hooks/Combo/useGetCombo"
import { comboMemory, catalogItemsMemory, productsMemory } from "../../../Objects/Memory/Memory"

interface PromotionApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  helpYoutubeLink?: string
  onClose: () => void
  onConfirm: (request: ApplyPromotionRequest) => Promise<void>
}

type OptionValue =
  | "all_products"
  | "selected_products"
  | "all_catalogItems"
  | "selected_catalogItems"
  | "all_combos"
  | "selected_combos"

export const PromotionApplyPopup: React.FC<PromotionApplyPopupProps> = ({
  title,
  actionLabel,
  helpTitle,
  helpDescription,
  helpBullets,
  helpKey,
  helpYoutubeLink,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("promotion")

  const { handleGetProducts } = useGetProducts()

  const { handleGetCatalogItems } = useGetCatalogItems()

  const { handleGetCombo } = useGetCombo()

  const [targetType, setTargetType] = useState<OptionValue>("all_products")

  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([])

  const [selectedCatalogItemIds, setSelectedCatalogItemIds] = useState<number[]>([])

  const [selectedComboIds, setSelectedComboIds] = useState<number[]>([])

  const [enableProductsSelection, setEnableProductsSelection] = useState(false)

  const [enableCatalogItemsSelection, setEnableCatalogItemsSelection] = useState(false)

  const [enableCombosSelection, setEnableCombosSelection] = useState(false)

  useEffect(() => {
    handleGetProducts(() => {})
    handleGetCatalogItems(() => {})
    handleGetCombo(() => {})
  }, [])

  const options = useMemo(
    () => [
      { label: t("apply_target_all_products"), value: "all_products" },
      { label: t("apply_target_selected_products"), value: "selected_products" },
      { label: t("apply_target_all_catalogItems"), value: "all_catalogItems" },
      { label: t("apply_target_selected_catalogItems"), value: "selected_catalogItems" },
      { label: t("apply_target_all_combos"), value: "all_combos" },
      { label: t("apply_target_selected_combos"), value: "selected_combos" },
    ],
    [t],
  )

  const handleChangeTarget = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as OptionValue
    setTargetType(value)

    if (value !== "selected_products") {
      setSelectedProductIds([])
    }

    if (value !== "selected_catalogItems") {
      setSelectedCatalogItemIds([])
    }

    if (value !== "selected_combos") {
      setSelectedComboIds([])
    }
  }

  const getRequest = (): ApplyPromotionRequest => {
    switch (targetType) {
      case "all_products":
        return { targetType: EPromotionApplyTargetType.AllProducts, productIds: [], catalogItemIds: [], comboIds: [] }
      case "selected_products":
        return {
          targetType: EPromotionApplyTargetType.SelectedProducts,
          productIds: selectedProductIds,
          catalogItemIds: [],
          comboIds: [],
        }
      case "all_catalogItems":
        return { targetType: EPromotionApplyTargetType.AllCatalogItems, productIds: [], catalogItemIds: [], comboIds: [] }
      case "selected_catalogItems":
        return {
          targetType: EPromotionApplyTargetType.SelectedCatalogItems,
          productIds: [],
          catalogItemIds: selectedCatalogItemIds,
          comboIds: [],
        }
      case "all_combos":
        return { targetType: EPromotionApplyTargetType.AllCombos, productIds: [], catalogItemIds: [], comboIds: [] }
      case "selected_combos":
        return {
          targetType: EPromotionApplyTargetType.SelectedCombos,
          productIds: [],
          catalogItemIds: [],
          comboIds: selectedComboIds,
        }
      default:
        return { targetType: EPromotionApplyTargetType.AllProducts, productIds: [], catalogItemIds: [], comboIds: [] }
    }
  }

  const shouldSelectProducts = targetType === "selected_products"

  const shouldSelectCatalogItems = targetType === "selected_catalogItems"

  const shouldSelectCombos = targetType === "selected_combos"

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faTags}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey={helpKey}
                    title={helpTitle}
                    variant="global"
                    description={helpDescription}
                    bullets={helpBullets}
                    youtubeLink={helpYoutubeLink}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"

                  descriptionKey="interactionDescriptions:pages.serivesDir.promotionApplyPopup.button.onClose" />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="p-2">
              <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-3">
                <GenericDropDown
                  legend={t("apply_target_legend")}
                  data={options}
                  initialState={targetType}
                  handlerFuncion={handleChangeTarget}

                  descriptionKey="interactionDescriptions:pages.serivesDir.promotionApplyPopup.select.select159" />
              </div>

              {shouldSelectProducts && (
                <SelectionSection
                  title={t("apply_select_products_action")}
                  countTitle={t("apply_selected_products")}
                  emptyLabel={t("apply_no_selected_products")}
                  selectedIds={selectedProductIds}
                  icon={faBoxOpen}
                  onAdd={() => setEnableProductsSelection(true)}
                  onRemove={(id) => setSelectedProductIds((prev) => prev.filter((itemId) => itemId !== id))}
                  getLabel={(id) => productsMemory.findById(id)?.catalogProductEntity.name ?? "-"}
                />
              )}

              {shouldSelectCatalogItems && (
                <SelectionSection
                  title={t("apply_select_catalogItems_action")}
                  countTitle={t("apply_selected_catalogItems")}
                  emptyLabel={t("apply_no_selected_catalogItems")}
                  selectedIds={selectedCatalogItemIds}
                  icon={faBoxesStacked}
                  onAdd={() => setEnableCatalogItemsSelection(true)}
                  onRemove={(id) => setSelectedCatalogItemIds((prev) => prev.filter((itemId) => itemId !== id))}
                  getLabel={(id) => catalogItemsMemory.findById(id)?.catalogProductEntity.name ?? "-"}
                />
              )}

              {shouldSelectCombos && (
                <SelectionSection
                  title={t("apply_select_combos_action")}
                  countTitle={t("apply_selected_combos")}
                  emptyLabel={t("apply_no_selected_combos")}
                  selectedIds={selectedComboIds}
                  icon={faLayerGroup}
                  onAdd={() => setEnableCombosSelection(true)}
                  onRemove={(id) => setSelectedComboIds((prev) => prev.filter((itemId) => itemId !== id))}
                  getLabel={(id) => comboMemory.findById(id)?.catalogProductEntity.name ?? "-"}
                />
              )}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                label={actionLabel}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                onClick={() => onConfirm(getRequest())}

                  descriptionKey="interactionDescriptions:pages.serivesDir.promotionApplyPopup.button.actionLabel" />
            </div>
          </div>
        </Animate>
      </div>

      {enableProductsSelection && (
        <ListSelections
          title={t("apply_target_selected_products")}
          icon={faBoxOpen}
          listCards={productsMemory.getListCards(true)}
          messageCount={t("list:label_products_page_message_count")}
          messageEmpty={t("list:label_products_page_message_empty")}
          onClinck={(id) => {
            setSelectedProductIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
            setEnableProductsSelection(false)
          }}
          onClonse={() => setEnableProductsSelection(false)}
        />
      )}

      {enableCatalogItemsSelection && (
        <ListSelections
          title={t("apply_target_selected_catalogItems")}
          icon={faBoxesStacked}
          listCards={catalogItemsMemory.getListCards(true)}
          messageCount={t("list:label_catalogItems_page_message_count")}
          messageEmpty={t("list:label_catalogItems_page_message_empty")}
          onClinck={(id) => {
            setSelectedCatalogItemIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
            setEnableCatalogItemsSelection(false)
          }}
          onClonse={() => setEnableCatalogItemsSelection(false)}
        />
      )}

      {enableCombosSelection && (
        <ListSelections
          title={t("apply_target_selected_combos")}
          icon={faLayerGroup}
          listCards={comboMemory.getListCards(true)}
          messageCount={t("list:label_combo_page_message_count")}
          messageEmpty={t("list:label_combo_page_message_empty")}
          onClinck={(id) => {
            setSelectedComboIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
            setEnableCombosSelection(false)
          }}
          onClonse={() => setEnableCombosSelection(false)}
        />
      )}
    </ModalPortal>
  )
}

const SelectionSection: React.FC<{
  title: string
  countTitle: string
  emptyLabel: string
  selectedIds: number[]
  icon: any
  onAdd: () => void
  onRemove: (id: number) => void
  getLabel: (id: number) => string
}> = ({ title, countTitle, emptyLabel, selectedIds, icon, onAdd, onRemove, getLabel }) => {
  const { t } = useTranslation("promotion")

  return (
    <>
      <GetSpace top={10} />

      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-orange-100 px-2 py-1 text-orange-600">
              <FontAwesomeIcon icon={icon} />
            </span>

            <span className="text-sm font-semibold text-slate-700">{title}</span>
          </div>

          <ButtonIcon
            typeButton="button"
            type="primary"
            size="medium"
            icon={faPlus}
            sizeNumber={SIZE.buttonCloseIconSize}
            bgColor={THEME.colors.buttonBGPlusColor}
            iconColor={THEME.colors.buttonFGPlusColor}
            onClick={onAdd}

                  descriptionKey="interactionDescriptions:pages.serivesDir.promotionApplyPopup.button.onAdd" />
        </div>

        <GetSpace top={5} />

        <CountElements count={selectedIds.length} title={countTitle} />

        <GetSpace top={5} />

        {selectedIds.length === 0 && <span className="text-xs text-slate-500">{emptyLabel}</span>}

        {selectedIds.map((id) => (
          <Animate key={id} type={AnimationTypes.fadeInTraslate} duration="1.2s">
            <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700" title={getLabel(id)}>
                {getLabel(id)}
              </span>

              <button
                className="rounded-full bg-red-50 p-1.5 transition-colors hover:bg-red-100"
                type="button"
                onClick={() => onRemove(id)}
                title={t("buttons:button_remove")}

                  data-interaction-description-key="interactionDescriptions:pages.serivesDir.promotionApplyPopup.button.onRemoveId">
                <FontAwesomeIcon icon={faTrashCan} color={THEME.colors.buttonBGRemoveColor} />
              </button>
            </div>
          </Animate>
        ))}
      </div>
    </>
  )
}
